import { action, observable } from "mobx";
import { isAppTypeIframe } from "../getAppType";
import { setPreviewTranslationOverrides } from "../localization/previewTranslationOverrides";
import { getImageSrcFromSettingValue } from "../url/getImageSrcFromSettingValue";
import { lp } from "../utils/logPrefix";
import { CvpSettings } from "./CvpSettings";
import { getDefaultSettings } from "./getDefaultSettings";

function compareSettings(a: CvpSettings, b: CvpSettings) {
  const keys = Object.keys(getDefaultSettings(isAppTypeIframe()));

  for (const key of keys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}

export const settings = observable.box<CvpSettings>(
  getDefaultSettings(isAppTypeIframe())
);

function separateSettingsAndTranslationOverrides(settings: CvpSettings) {
  const translationOverrides: any = {};
  const _settings: any = {};

  // for key value
  for (const key in settings) {
    const value = settings[key];

    const isOverride = !!value?.idText && !!value?.systemText;

    if (isOverride) {
      translationOverrides[key] = value;
    } else {
      _settings[key] = value;
    }
  }

  return { translationOverrides, _settings };
}

export const setSettingsComplete = action((s: CvpSettings) => {
  const { translationOverrides, _settings } =
    separateSettingsAndTranslationOverrides(s);

  setPreviewTranslationOverrides(Object.values(translationOverrides));

  if (compareSettings(settings.get(), _settings)) {
    updateFavicon();
    updateFontLink();
    updateFontFamily();

    return settings.get();
  }

  settings.set(_settings);
  settingsSetCallbacks.forEach((cb) => cb());

  updateFavicon();
  updateFontLink();
  updateFontFamily();
  return _settings;
});

const settingsSetCallbacks: Array<() => void> = [];

export const addSettingsSetCallback = (cb: () => void) => {
  settingsSetCallbacks.push(cb);
  return () => removeSettingsSetCallback(cb);
};

export const removeSettingsSetCallback = (cb: () => void) => {
  const index = settingsSetCallbacks.indexOf(cb);
  if (index > -1) {
    settingsSetCallbacks.splice(index, 1);
  }
};

function updateFavicon() {
  const value = getImageSrcFromSettingValue(
    settings.get().favicon ?? "",
    window.cvpIdConcern
  );

  const newEl = document.createElement("link");
  newEl.setAttribute("href", value ?? "");

  const currentEl = document.getElementById("cvp-favicon");

  const _current = currentEl?.getAttribute("href") ?? "";
  const _new = newEl.getAttribute("href") ?? "";

  if (_current !== _new) {
    console.log(lp(), "Updating favicon", {
      _current,
      _new,
    });

    currentEl?.remove?.();

    newEl.id = "cvp-favicon";
    newEl.rel = "icon";
    newEl.type = "image/x-icon";

    document.head.appendChild(newEl);
  }
}

function updateFontLink() {
  const value = settings.get().fontLink ?? "";
  const newEl = document.createElement("link");
  newEl.setAttribute("href", value);

  // Only allow google fonts
  if (!newEl.href.startsWith("https://fonts.googleapis.com/")) {
    newEl.href = "";
  }

  const currentEl = document.getElementById("cvp-font-link");

  const _current = currentEl?.getAttribute("href") ?? "";
  const _new = newEl.getAttribute("href") ?? "";

  if (_current !== _new) {
    console.log(lp(), "Updating font link", {
      _current,
      _new,
    });

    currentEl?.remove?.();

    newEl.id = "cvp-font-link";
    newEl.rel = "stylesheet";
    document.head.appendChild(newEl);
  }
}

function updateFontFamily() {
  const value = settings.get().fontFamily ?? "";
  const newEl = document.createElement("style");
  newEl.innerText = `:root:root{--font-family:${value};}`;

  const currentEl = document.getElementById("cvp-font-family");

  const _current = currentEl?.innerText ?? "";
  const _new = newEl.innerText ?? "";

  if (_current !== _new) {
    console.log(lp(), "Updating font family", {
      _current,
      _new,
    });

    currentEl?.remove?.();

    newEl.id = "cvp-font-family";
    document.head.appendChild(newEl);
  }
}
